import { CalendarDays, LayoutDashboard, Settings, Sparkles, Users } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCoachPendingTranscriptReviewCount } from "@/hooks/useCoachPendingTranscriptReviewCount";

const navItems = [
  { title: "Overview", url: "/coach/overview", icon: LayoutDashboard },
  { title: "Leaders", url: "/coach/leaders", icon: Users, showLeaderBadge: true },
  { title: "Coach Hub", url: "/coach/hub", icon: Sparkles },
  { title: "Meetings", url: "/coach/meetings", icon: CalendarDays },
  { title: "Settings", url: "/coach/settings", icon: Settings },
];

function getCoachTourId(url: string) {
  if (url === "/coach/overview") return "coach-tour-overview";
  if (url === "/coach/leaders") return "coach-tour-leaders";
  if (url === "/coach/meetings") return "coach-tour-meetings";
  if (url === "/coach/hub") return "coach-tour-veera";
  return undefined;
}

export function CoachBottomNav() {
  const location = useLocation();
  const { leadersWithPendingTranscriptReviewCount } = useCoachPendingTranscriptReviewCount();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-md safe-bottom">
      <div className="mx-auto flex h-16 max-w-md items-center gap-1 px-2">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.url ||
            (item.url === "/coach/leaders" && location.pathname.startsWith("/coach/leaders")) ||
            (item.url === "/coach/meetings" &&
              (location.pathname === "/coach/meetings" ||
                location.pathname === "/coach/calendar" ||
                location.pathname === "/coach/calls")) ||
            (item.url === "/coach/hub" &&
              (location.pathname === "/coach/hub" ||
                location.pathname.startsWith("/coach/hub/") ||
                location.pathname === "/coach/ai" ||
                location.pathname === "/coach/veera" ||
                location.pathname.startsWith("/coach/identity") ||
                location.pathname.startsWith("/coach/philosophy") ||
                location.pathname.startsWith("/coach/my-voice"))) ||
            (item.url === "/coach/settings" && location.pathname.startsWith("/coach/settings"));
          const badgeCount = item.showLeaderBadge ? leadersWithPendingTranscriptReviewCount : 0;

          return (
            <Link
              key={item.url}
              to={item.url}
              aria-current={isActive ? "page" : undefined}
              data-tour-id={getCoachTourId(item.url)}
              className={cn(
                "relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-2 transition-all duration-200",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <div className="relative">
                <item.icon className={cn("h-5 w-5 transition-transform duration-200", isActive && "scale-110")} />
                {badgeCount > 0 && (
                  <span
                    className={cn(
                      "absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold",
                      isActive ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground",
                    )}
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}
              </div>
              <span className={cn("max-w-full truncate text-[10px]", isActive ? "font-medium" : "font-normal")}>
                {item.title}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
